import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-provenance-marker>` says about itself.
 *
 * Wherever a surface shows a figure nobody typed, this is the difference between a number somebody
 * can defend and one they cannot. The figure on screen is the same either way; what changes is
 * whether the person reading it can say where it came from. Four origins, and the vocabulary is the
 * point:
 *
 *   computed   — a calculation produced it from other values. Running it again produces it again.
 *   overridden — a person pinned a value over the calculated one. It will NOT come back on its own,
 *                and it draws the override tokens — one fixed colour held apart from the brand
 *                accent, so a pinned value reads the same in every tenant and in both themes.
 *   synced     — it is mirrored from another system and changing it here changes nothing there.
 *   inherited  — it flowed down from a parent or a default and nobody has touched it.
 *
 * Two things to know about how it takes its content. There is NO `<slot>` — the text comes in
 * through the `label` attribute, and light-DOM text put inside the element is never drawn. (The old
 * hand-kept example did exactly that, passing the source name as a text child of an element with
 * nowhere to put it, so the marker rendered as the bare word "Computed". Both examples below use
 * the attribute.) And `label` unset falls back to the origin's own name, while `label` set to an
 * empty string leaves a glyph with no word and an accessible name ending in a colon, which is not a
 * useful state to ship.
 */
const docs: ComponentDocs = {
  aliases: ['origin badge', 'source indicator', 'override flag', 'pinned value', 'lineage badge', 'computed marker'],

  answers: [
    'show that a number was calculated rather than typed',
    'flag an amount somebody pinned by hand',
    'show a field mirrored from another system',
    'show that a value came from a default nobody has changed',
    'defend a figure when somebody asks where it came from',
  ],

  placements: ['recordPage', 'appPage', 'screen'],

  inputs: [
    {
      name: 'origin',
      attribute: 'origin',
      type: 'text',
      options: ['computed', 'overridden', 'inherited', 'synced'],
      description:
        'Where the value came from: computed, overridden, inherited or synced. It picks the glyph, the ' +
        'tint and — unless a label is given — the word. An unrecognised value draws the inherited ' +
        'treatment.',
      default: 'inherited',
    },
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description:
        'The word on the marker, replacing the origin\'s own name. Use it to say WHICH source — ' +
        '"Computed from the rate table", "Synced from the billing system" — rather than to rename the ' +
        'origin. Unset, it draws Computed / Overridden / Inherited / Synced; set to an empty string it ' +
        'draws nothing and leaves an empty accessible name.',
      default: "the origin's own name",
    },
  ],

  // Read from the source: it reports nothing. Changing the value is somebody else's control; this
  // marker only states where the value standing there came from.
  events: [],

  // No `<slot>` is rendered — see the header. Text placed inside the element is not drawn.
  state: [],

  slots: [],

  tokens: [
    { token: '--caos-color-surface-2', controls: 'The fill of the inherited (default) marker.' },
    { token: '--caos-color-text-muted', controls: 'Its lettering.' },
    { token: '--caos-color-border', controls: 'Its edge.' },
    { token: '--caos-color-info-bg', controls: 'The fill of a computed marker; the edge is mixed from --caos-color-info.' },
    { token: '--caos-color-info', controls: 'The colour a computed marker\'s edge is mixed from.' },
    { token: '--caos-color-info-text', controls: 'Its lettering — the on-tint ink, so it holds up in both themes.' },
    { token: '--caos-color-success-bg', controls: 'The fill of a synced marker.' },
    { token: '--caos-color-success', controls: 'The colour its edge is mixed from.' },
    { token: '--caos-color-success-text', controls: 'Its lettering.' },
    { token: '--caos-color-override-bg', controls: 'The fill of an overridden marker — the one override colour, fixed across brands and themes.' },
    { token: '--caos-color-override-border', controls: 'Its edge.' },
    { token: '--caos-color-override-text', controls: 'Its lettering.' },
    { token: '--caos-radius-pill', controls: 'The rounded ends of the marker.' },
    { token: '--caos-font', controls: 'The typeface the word is set in.' },
    { token: '--caos-space-2', controls: 'The horizontal padding.' },
    { token: '--caos-space-1', controls: 'The gap between the glyph and the word.' },
  ],

  parts: [
    { part: 'marker', is: 'the marker' },
    { part: 'icon', is: 'the origin glyph' },
    { part: 'label', is: 'the origin text' },
  ],

  methods: [],

  placing:
    'Beside the value it qualifies, not beside the field label — in the value half of a key/value row, ' +
    'at the end of a table cell, after a figure in a total. It is `inline-flex` and vertically ' +
    'centred, so it sits on a line of figures without moving them. One per value: a marker on every ' +
    'row of a table says nothing, so mark the values whose origin is NOT the obvious one.',

  whenToUse:
    'A number or a value whose origin changes what a person should do with it — an amount they may be ' +
    'asked to justify, a figure that will be recalculated, a field that is really owned by another ' +
    'system.',

  insteadUse: [
    {
      name: 'StatusPill',
      when:
        'The subject is the RECORD and where it has got to, not one value and where it came from. A ' +
        'record is In review; a number is Computed.',
    },
    {
      name: 'Tag',
      when: 'The chip is a label a person applied to the record rather than a statement about a value.',
    },
    {
      name: 'InlineHint',
      when:
        'What needs saying is what the field DRIVES rather than where its value came from — guidance ' +
        'that is true whoever filled it in.',
    },
    {
      name: 'NoticeBanner',
      when:
        'The provenance applies to the whole surface — every figure on the page is at pinned rates, ' +
        'say — rather than to one value. Marking forty rows individually says less than one banner.',
    },
  ],

  accessibility:
    'The marker is a `role="img"` named `Value origin: <label>`, so it is announced as one statement ' +
    'attached to the value rather than as a loose word after a number, and the glyph is hidden because ' +
    'the label already carries it. The tint is never the only signal: the word is always drawn and ' +
    'always in the name. Left to the author: not blanking the label (an empty one leaves a coloured ' +
    'glyph and an accessible name with nothing after the colon), and making sure the marker follows ' +
    'the value it belongs to in reading order rather than floating at the end of the row.',

  examples: [
    {
      key: 'origins',
      label: 'The four origins',
      summary:
        'The whole vocabulary side by side, with the default word each origin draws — the set to choose ' +
        'from when deciding what a surface needs to say about its figures.',
      placement: [
        { component: 'caos-provenance-marker', inputs: { origin: 'computed' } },
        { component: 'caos-provenance-marker', inputs: { origin: 'overridden' } },
        { component: 'caos-provenance-marker', inputs: { origin: 'synced' } },
        { component: 'caos-provenance-marker', inputs: { origin: 'inherited' } },
      ],
    },
    {
      key: 'named-sources',
      label: 'Naming the actual source',
      summary:
        'The same markers carrying the specific source instead of the generic word, which is what makes ' +
        'a figure defensible: not "Overridden" but overridden by whom, not "Synced" but synced from ' +
        'where.',
      placement: [
        { component: 'caos-provenance-marker', inputs: { origin: 'computed', label: 'Computed from the rate table' } },
        { component: 'caos-provenance-marker', inputs: { origin: 'overridden', label: 'Pinned by Dana Whitfield' } },
        { component: 'caos-provenance-marker', inputs: { origin: 'synced', label: 'Synced from the billing system' } },
        { component: 'caos-provenance-marker', inputs: { origin: 'inherited', label: 'From the account default' } },
      ],
    },
  ],
};

export default docs;
